import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Search, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtZAR = (v) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(Number(v || 0));

const STATUS_COLORS = {
  settled: '#059669',   // emerald-600
  current: '#0284c7',   // sky-600
  overdue: '#dc2626',   // red-600
};

function useAuthHeader() {
  return useMemo(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
}

export default function ClientLoanLedgerSummary({ clientId, onOpenLoan }) {
  const navigate = useNavigate();
  const authHeader = useAuthHeader();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('loan_number');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await axios.get(
          `${API_URL}/api/admin/client-loan-ledger/summary/${encodeURIComponent(clientId)}`,
          { headers: authHeader }
        );
        if (!cancelled) setSummary(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, authHeader]);

  const items = summary?.items || [];

  // ── Linked filters, crossfilter style (user 2026-10-19) ─────────────────
  // Clicking a pie slice, a due-month bar or a tenor bar filters EVERY other
  // part of the page (KPIs, the other charts, the table). Each chart is drawn
  // from the loans that pass all the OTHER filters, so it shows what is left
  // to drill into. Clicking the same slice/bar again clears that filter.
  const [dueMonth, setDueMonth] = useState(null);
  const [tenorBucket, setTenorBucket] = useState(null);
  const todayIso = new Date().toISOString().slice(0, 10);
  const dueMonthOf = (i) => (i.expected_settlement ? String(i.expected_settlement).slice(0, 7) : 'No date');
  const tenorOf = (i) => {
    const t = i.achieved_tenor_days;
    if (t === null || t === undefined) return null;
    const b = TENOR_BUCKETS.find((x) => t >= x.min && t <= x.max);
    return b ? b.bucket : null;
  };
  const passes = (i, skip) => (
    (skip === 'status' || statusFilter === 'all' || i.status === statusFilter)
    && (skip === 'due' || !dueMonth || dueMonthOf(i) === dueMonth)
    && (skip === 'tenor' || !tenorBucket || tenorOf(i) === tenorBucket)
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let out = items.filter((i) => passes(i));
    if (q) {
      out = out.filter((i) => i.loan_number.toLowerCase().includes(q));
    }
    out.sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filter, statusFilter, dueMonth, tenorBucket, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const pickStatus = (key) => {
    const next = statusFilter === key ? 'all' : key;
    setStatusFilter(next);
    // Current / overdue: show the loans in due-date order.
    if (next === 'current' || next === 'overdue') { setSortField('expected_settlement'); setSortDir('asc'); }
  };
  const clearAll = () => { setStatusFilter('all'); setDueMonth(null); setTenorBucket(null); setFilter(''); };

  const openLoan = (ln) => {
    if (onOpenLoan) onOpenLoan(ln);
    else navigate(`/admin/clients/${clientId}/loan-ledger?loan=${ln}`);
  };

  if (loading && !summary) {
    return <Card className="bg-white border-slate-200 p-8 text-center text-slate-500 shadow-sm">Loading summary…</Card>;
  }
  if (!summary || items.length === 0) {
    return <Card className="bg-white border-slate-200 p-8 text-center text-slate-500 shadow-sm">No loans found for this client.</Card>;
  }

  // KPIs follow every filter.
  const kpiSet = filtered;
  const sum = (arr, f) => arr.reduce((t, i) => t + Number(i[f] || 0), 0);
  const outstandingTotal = sum(kpiSet, 'outstanding');
  const rateBase = outstandingTotal > 0 ? 'outstanding' : 'capital_deployed';
  const rateDen = sum(kpiSet, rateBase);
  const weightedRate = rateDen > 0
    ? kpiSet.reduce((t, i) => t + Number(i.interest_rate_pct || 0) * Number(i[rateBase] || 0), 0) / rateDen : 0;
  const tenors = kpiSet.map((i) => i.achieved_tenor_days).filter((t) => t !== null && t !== undefined);
  const avgTenor = tenors.length ? Math.round(tenors.reduce((a, b) => a + b, 0) / tenors.length) : 0;
  const overdueCount = kpiSet.filter((i) => i.status === 'overdue').length;

  // Status mix: loans passing the due/tenor filters.
  const forStatus = items.filter((i) => passes(i, 'status'));
  const statusData = ['settled', 'current', 'overdue'].map((k) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), key: k,
    value: forStatus.filter((i) => i.status === k).length,
  })).filter((d) => d.value > 0);

  // When it's due: outstanding by due month, for open loans passing the
  // status/tenor filters (settled loans have nothing due).
  const dueMap = {};
  items.filter((i) => passes(i, 'due') && i.status !== 'settled').forEach((i) => {
    const m = dueMonthOf(i);
    dueMap[m] = dueMap[m] || { month: m, outstanding: 0, count: 0 };
    dueMap[m].outstanding += Number(i.outstanding || 0);
    dueMap[m].count += 1;
  });
  const dueData = Object.values(dueMap).sort((a, b) => (a.month > b.month ? 1 : -1));

  const tenorBuckets = bucketTenors(items.filter((i) => passes(i, 'tenor')));
  const anyFilter = statusFilter !== 'all' || dueMonth || tenorBucket || filter.trim();
  const daysToDue = (i) => {
    if (!i.expected_settlement || i.status === 'settled') return null;
    return Math.round((new Date(String(i.expected_settlement).slice(0, 10)) - new Date(todayIso)) / 86400000);
  };

  return (
    <div className="space-y-6" data-testid="client-loan-summary">
      {/* KPIs — follow the active filters */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Loans" value={kpiSet.length} testid="kpi-loan-count" />
        <Kpi label="Deployed" value={fmtZAR(sum(kpiSet, 'capital_deployed'))} testid="kpi-deployed" accent="emerald" />
        <Kpi label="Outstanding" value={fmtZAR(outstandingTotal)} testid="kpi-outstanding" accent="sky" />
        <Kpi label="Weighted Rate" value={`${weightedRate.toFixed(2)}%`} testid="kpi-rate" />
        <Kpi label="Avg Tenor" value={`${avgTenor} d`} testid="kpi-tenor" />
        <Kpi label="Overdue" value={overdueCount} testid="kpi-overdue" accent={overdueCount > 0 ? 'red' : 'slate'} />
      </div>

      {anyFilter && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="summary-active-filters">
          <span className="text-slate-500">Showing</span>
          {statusFilter !== 'all' && <FilterChip label={`Status: ${statusFilter}`} onClear={() => setStatusFilter('all')} />}
          {dueMonth && <FilterChip label={`Due: ${dueMonth}`} onClear={() => setDueMonth(null)} />}
          {tenorBucket && <FilterChip label={`Tenor: ${tenorBucket}`} onClear={() => setTenorBucket(null)} />}
          {filter.trim() && <FilterChip label={`Loan #: ${filter.trim()}`} onClear={() => setFilter('')} />}
          <button className="text-sky-700 underline" onClick={clearAll} data-testid="summary-clear-filters">Clear all</button>
        </div>
      )}

      {/* Charts — click to drill down */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200 p-4 shadow-sm" data-testid="status-chart">
          <div className="text-sm font-semibold text-slate-800 mb-2">Status Mix <span className="font-normal text-slate-400 text-xs">click a slice</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}
                onClick={(d) => pickStatus(d?.payload?.key || d?.key)} cursor="pointer">
                {statusData.map((d) => (
                  <Cell key={d.key} fill={STATUS_COLORS[d.key]}
                    fillOpacity={statusFilter === 'all' || statusFilter === d.key ? 1 : 0.25}
                    data-testid={`status-slice-${d.key}`} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="bg-white border-slate-200 p-4 shadow-sm" data-testid="due-chart">
          <div className="text-sm font-semibold text-slate-800 mb-2">When it's due <span className="font-normal text-slate-400 text-xs">outstanding by due month · click a bar</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dueData} onClick={(e) => e?.activeLabel && setDueMonth(dueMonth === e.activeLabel ? null : e.activeLabel)}>
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
              <Tooltip formatter={(v, n, p) => [`${fmtZAR(v)} · ${p?.payload?.count} loan(s)`, 'Outstanding']} />
              <Bar dataKey="outstanding" radius={[4, 4, 0, 0]} cursor="pointer">
                {dueData.map((d) => (
                  <Cell key={d.month} fill={d.month < todayIso.slice(0, 7) ? STATUS_COLORS.overdue : STATUS_COLORS.current}
                    fillOpacity={!dueMonth || dueMonth === d.month ? 1 : 0.25} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="bg-white border-slate-200 p-4 shadow-sm" data-testid="tenor-chart">
          <div className="text-sm font-semibold text-slate-800 mb-2">Achieved Tenor (days) <span className="font-normal text-slate-400 text-xs">click a bar</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tenorBuckets} onClick={(e) => e?.activeLabel && setTenorBucket(tenorBucket === e.activeLabel ? null : e.activeLabel)}>
              <XAxis dataKey="bucket" fontSize={11} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer">
                {tenorBuckets.map((b) => (
                  <Cell key={b.bucket} fill="#059669" fillOpacity={!tenorBucket || tenorBucket === b.bucket ? 1 : 0.25} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-500" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter loan numbers…"
            className="pl-8"
            data-testid="summary-filter-input"
          />
        </div>
        <div className="flex items-center gap-1 text-xs">
          {['all', 'current', 'overdue', 'settled'].map((st) => (
            <button
              key={st}
              onClick={() => (st === 'all' ? setStatusFilter('all') : pickStatus(st))}
              className={`px-3 py-1 rounded-full border ${
                statusFilter === st
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
              }`}
              data-testid={`summary-status-${st}`}
            >
              {st.charAt(0).toUpperCase() + st.slice(1)}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-slate-500">{filtered.length} of {items.length} loans</div>
      </div>

      {/* Loans table */}
      <Card className="bg-white border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-600 bg-slate-100">
              <tr>
                <Th onClick={() => toggleSort('loan_number')} active={sortField === 'loan_number'} dir={sortDir}>Loan #</Th>
                <Th className="text-right" onClick={() => toggleSort('capital_deployed')} active={sortField === 'capital_deployed'} dir={sortDir}>Deployed</Th>
                <Th className="text-right" onClick={() => toggleSort('outstanding')} active={sortField === 'outstanding'} dir={sortDir}>Outstanding</Th>
                <Th className="text-right" onClick={() => toggleSort('interest_rate_pct')} active={sortField === 'interest_rate_pct'} dir={sortDir}>Rate</Th>
                <Th className="text-center">First Draw</Th>
                <Th className="text-center" onClick={() => toggleSort('expected_settlement')} active={sortField === 'expected_settlement'} dir={sortDir}>Due (Expected Settle)</Th>
                <Th className="text-right">Days to due</Th>
                <Th className="text-right" onClick={() => toggleSort('achieved_tenor_days')} active={sortField === 'achieved_tenor_days'} dir={sortDir}>Achieved / Orig Tenor</Th>
                <Th className="text-center" onClick={() => toggleSort('status')} active={sortField === 'status'} dir={sortDir}>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((i) => {
                const dtd = daysToDue(i);
                return (
                <tr
                  key={i.loan_number}
                  onClick={() => openLoan(i.loan_number)}
                  className="cursor-pointer hover:bg-emerald-50/60"
                  data-testid={`summary-row-${i.loan_number}`}
                >
                  <td className="px-3 py-2 font-mono text-emerald-700 hover:underline">{i.loan_number}</td>
                  <td className="px-3 py-2 text-right">{fmtZAR(i.capital_deployed)}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmtZAR(i.outstanding)}</td>
                  <td className="px-3 py-2 text-right">{Number(i.interest_rate_pct || 0).toFixed(2)}%</td>
                  <td className="px-3 py-2 text-center text-slate-600 font-mono text-xs">{i.first_drawdown || '—'}</td>
                  <td className="px-3 py-2 text-center text-slate-600 font-mono text-xs">{i.expected_settlement || '—'}</td>
                  <td className={`px-3 py-2 text-right font-mono text-xs ${dtd !== null && dtd < 0 ? 'text-red-700' : 'text-slate-700'}`}>
                    {dtd === null ? '—' : dtd < 0 ? `${-dtd} d late` : `${dtd} d`}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    <span className="text-slate-900">{i.achieved_tenor_days ?? '—'}</span>
                    <span className="text-slate-400"> / {i.original_tenor_days ?? '—'} d</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <StatusPill status={i.status} daysOverdue={i.days_overdue} />
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center text-slate-500">No loans match the current filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 text-white">
      {label}
      <button onClick={onClear} className="ml-1 text-white/70 hover:text-white" aria-label="clear">×</button>
    </span>
  );
}

function Kpi({ label, value, testid, accent = 'slate' }) {
  const accentClass = {
    emerald: 'text-emerald-700',
    sky: 'text-sky-700',
    red: 'text-red-700',
    slate: 'text-slate-900',
  }[accent];
  return (
    <Card className="bg-white border-slate-200 p-3 shadow-sm" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${accentClass} truncate`}>{value ?? '—'}</div>
    </Card>
  );
}

function Th({ children, className = '', onClick, active, dir }) {
  return (
    <th
      onClick={onClick}
      className={`px-3 py-2 ${className} ${onClick ? 'cursor-pointer select-none hover:text-slate-900' : ''} ${active ? 'text-slate-900' : ''}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {onClick && <ArrowUpDown className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />}
        {active && <span className="text-[10px]">{dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );
}

function StatusPill({ status, daysOverdue }) {
  const cfg = {
    settled: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', label: 'Settled' },
    current: { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-300', label: 'Current' },
    overdue: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: `Overdue ${daysOverdue ? `${daysOverdue}d` : ''}` },
  }[status] || { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-300', label: status };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

const TENOR_BUCKETS = [
  { bucket: '0-30d',   min: 0,    max: 30 },
  { bucket: '31-90d',  min: 31,   max: 90 },
  { bucket: '91-180d', min: 91,   max: 180 },
  { bucket: '181-365d', min: 181, max: 365 },
  { bucket: '1-2y',    min: 366,  max: 730 },
  { bucket: '2y+',     min: 731,  max: 99999 },
];

function bucketTenors(items) {
  const buckets = TENOR_BUCKETS.map((b) => ({ ...b, count: 0 }));
  items.forEach((i) => {
    const t = i.achieved_tenor_days;
    if (t === null || t === undefined) return;
    const b = buckets.find((x) => t >= x.min && t <= x.max);
    if (b) b.count += 1;
  });
  return buckets;
}
